/**
 * ChatLinkedArtifacts - Display linked artifacts in chat input area
 * Shows checkmark + artifact name when linked, using artifact category colors
 * for visual pairing with conversation tabs
 */

import { Button } from '@/components/ui';
import { ConversationLink } from '@aerotage/shared';
import { useArtifactColors } from '../../hooks/useArtifactColors';

interface ChatLinkedArtifactsProps {
  links: ConversationLink[];
  onUnlink: (artifactId: string) => void;
  onUnlinkAll: () => void;
  effectiveMode: string;
  disabled?: boolean;
}

// Checkmark icon - inherits color from parent
const CheckIcon = () => (
  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

// X icon for unlink
const UnlinkIcon = () => (
  <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

export default function ChatLinkedArtifacts({ 
  links, 
  onUnlink, 
  onUnlinkAll,
  effectiveMode: _effectiveMode,
  disabled 
}: ChatLinkedArtifactsProps) {
  const { getClassesForArtifact } = useArtifactColors();
  
  if (links.length === 0) {
    return (
      <span className="text-xs text-muted-foreground">
        No artifact linked
      </span>
    );
  }
  
  const primaryLink = links[0];
  
  // Get artifact color classes for the primary linked artifact
  const artifactClasses = getClassesForArtifact(primaryLink.artifactId);
  
  return (
    <div className="flex items-center gap-1.5">
      {/* Linked indicator using artifact category colors - matches tab styling */}
      <div className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium border-l-3 ${artifactClasses.bg} ${artifactClasses.text} ${artifactClasses.borderLeft}`}>
        <CheckIcon />
        <span className="max-w-[150px] truncate" title={primaryLink.artifactName}>
          {primaryLink.artifactName}
        </span>
        {links.length > 1 && (
          <span className="opacity-70">+{links.length - 1}</span>
        )}
        {/* Unlink button - inside the badge */}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => links.length > 1 ? onUnlinkAll() : onUnlink(primaryLink.artifactId)}
          disabled={disabled}
          className={`w-4 h-4 p-0 ${artifactClasses.text} opacity-60 hover:opacity-100 hover:bg-muted/50`}
          title={links.length > 1 ? 'Unlink all' : `Unlink ${primaryLink.artifactName}`}
        >
          <UnlinkIcon />
        </Button>
      </div>
    </div>
  );
}
